/**
 * OSINT Playground — Phase 0 + Phase 1
 * Case list, case view with graph + inspector + input panel
 * Uses React Flow for smooth 2D graph visualization
 */
import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
  type OnInit,
} from 'reactflow'
import 'reactflow/dist/style.css'
import { apiClient } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Plus, Trash2, FileText, MousePointer2, Zap, Maximize2, Link2, CheckCircle2, XCircle, Loader2, RefreshCw, LayoutGrid, Circle, Rows, Send, Users, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import EntityNode from '@/components/playground/EntityNode'
import { GroupScrapeResults } from '@/components/playground/GroupScrapeResults'
import { useToast } from '@/hooks/use-toast'
import { useBackgroundJobs } from '@/contexts/BackgroundJobsContext'

/** Human-readable evidence summary (no raw JSON) */
function EvidenceSummary({ ev }: { ev: any }) {
  const st = ev?.source_type || 'other'
  const raw = ev?.raw || {}

  if (st === 'manual') {
    return <span className="text-muted-foreground">Added manually</span>
  }
  if (st === 'pivot') {
    const pivotId = raw.pivot_id || 'pivot'
    if (pivotId === 'breach') {
      const list = raw.List || raw.list || {}
      const names = typeof list === 'object' && !Array.isArray(list) ? Object.keys(list).filter((k: string) => !['', 'no results found', 'no results', 'not found'].includes(k.toLowerCase())) : []
      if (names.length > 0) {
        return (
          <span>
            Found in {names.length} breach{names.length > 1 ? 'es' : ''}: {names.slice(0, 3).join(', ')}
            {names.length > 3 && ` +${names.length - 3} more`}
          </span>
        )
      }
      return <span className="text-muted-foreground">Breach check — no exposures found</span>
    }
    if (pivotId === 'whois') {
      const r = raw.registrant || raw.registrar
      return r ? <span>WHOIS: {String(r)}</span> : <span className="text-muted-foreground">WHOIS lookup</span>
    }
    if (pivotId === 'serp') {
      const items = raw.items || []
      const count = Array.isArray(items) ? items.length : 0
      return count > 0 ? (
        <span>Found {count} web result{count !== 1 ? 's' : ''}</span>
      ) : (
        <span className="text-muted-foreground">Web search — no results or not configured</span>
      )
    }
    if (pivotId === 'rocketreach_enrich' || pivotId === 'rocketreach_search' || pivotId === 'rocketreach_linkedin') {
      const status = raw.status
      if (status === 'progress' || status === 'searching' || status === 'waiting') {
        return <span className="text-amber-600">RocketReach — lookup in progress (async)</span>
      }
      const name = raw.name
      return name ? <span>RocketReach: {String(name)}</span> : <span className="text-muted-foreground">RocketReach — not found or not configured</span>
    }
    if (pivotId === 'eyecon') {
      const d = raw.data
      const name = d?.fullName ?? d?.full_name
      return name ? <span>Eyecon: {String(name)}</span> : <span className="text-muted-foreground">Eyecon — not found or not configured</span>
    }
    if (pivotId === 'telegram_reverse') {
      const err = raw.error
      if (!raw.success) {
        const short = err && err.length > 60 ? `${err.slice(0, 57)}...` : err
        return (
          <span className="text-muted-foreground" title={err || ''}>
            Telegram — {short || 'not found'}
          </span>
        )
      }
      const pd = raw.parsed_data || {}
      const txt = raw.raw_response || ''
      const phone = pd.phone_number || (txt.match(/\+?\d{10,15}/)?.[0])
      const name = pd.name || pd.username
      if (phone || name) {
        return <span>Telegram: {[name, phone].filter(Boolean).join(' • ') || 'Lookup completed'}</span>
      }
      const preview = txt ? txt.slice(0, 80).replace(/\n/g, ' ') + (txt.length > 80 ? '…' : '') : ''
      return (
        <span className="text-muted-foreground" title={preview || 'No extractable data'}>
          Telegram — no data extracted{preview ? `: "${preview}"` : ''}
        </span>
      )
    }
    if (pivotId === 'epieos') {
      const gaia = raw.gaia_id
      return gaia ? <span>Gmail OSINT: GAIA {String(gaia)}</span> : <span className="text-muted-foreground">Gmail OSINT — no profile or not Gmail</span>
    }
    if (pivotId === 'x_profile') {
      const name = raw.name ?? raw.displayName ?? raw.username
      const followers = raw.followers ?? raw.followersCount
      return name ? (
        <span>X: {String(name)}{followers != null ? ` · ${followers} followers` : ''}</span>
      ) : (
        <span className="text-muted-foreground">X profile — not found or not configured</span>
      )
    }
    if (pivotId === 'x_posts_recent') {
      const tweets = raw.tweets ?? []
      const n = Array.isArray(tweets) ? tweets.length : 0
      return n > 0 ? (
        <span>X: {n} recent post{n !== 1 ? 's' : ''}</span>
      ) : (
        <span className="text-muted-foreground">X posts — no posts or not configured</span>
      )
    }
    if (pivotId === 'reddit_user') {
      const posts = raw.posts ?? []
      const n = Array.isArray(posts) ? posts.length : 0
      return n > 0 ? (
        <span>Reddit: {n} post{n !== 1 ? 's' : ''}</span>
      ) : (
        <span className="text-muted-foreground">Reddit — no posts or not configured</span>
      )
    }
    if (pivotId === 'ig_profile') {
      const user = raw.user
      const posts = raw.posts ?? []
      const n = Array.isArray(posts) ? posts.length : 0
      const un = user?.username ?? raw.username
      if (user || n > 0) {
        return <span>Instagram: {un || 'profile'}{n > 0 ? ` · ${n} posts` : ''}</span>
      }
      return <span className="text-muted-foreground">Instagram — not found or not configured</span>
    }
    if (pivotId === 'tiktok_profile') {
      const posts = raw.posts ?? []
      const n = Array.isArray(posts) ? posts.length : 0
      return n > 0 ? (
        <span>TikTok: {n} video{n !== 1 ? 's' : ''}</span>
      ) : (
        <span className="text-muted-foreground">TikTok — no videos or not configured</span>
      )
    }
    return <span className="text-muted-foreground">{pivotId} pivot</span>
  }
  if (st === 'whois') return <span className="text-muted-foreground">WHOIS data</span>
  if (st === 'leakosint') return <span className="text-muted-foreground">LeakOSINT</span>
  return <span className="text-muted-foreground">{st}</span>
}

/** Extract URLs from evidence raw for various pivot types */
function extractUrlsFromEvidence(ev: any): string[] {
  const raw = ev?.raw || {}
  const urls: string[] = []
  if (ev?.source_url) urls.push(ev.source_url)
  const items = raw.items ?? raw.posts ?? raw.tweets
  if (Array.isArray(items)) {
    for (const it of items) {
      const link = it?.link ?? it?.url
      if (typeof link === 'string' && link.startsWith('http')) urls.push(link)
    }
  }
  const link = raw.link ?? raw.url
  if (typeof link === 'string' && link.startsWith('http')) urls.push(link)
  return [...new Set(urls)]
}

/** Phase 4: Evidence card with View raw, Open link, source confidence */
function EvidenceCard({ ev, expanded, onToggle }: { ev: any; expanded: boolean; onToggle: () => void }) {
  const urls = extractUrlsFromEvidence(ev)
  const conf = ev?.source_confidence || 'low'
  const confColor = conf === 'high' ? 'text-green-600' : conf === 'medium' ? 'text-amber-600' : 'text-muted-foreground'
  return (
    <div className="rounded border p-2 text-xs bg-muted/30 flex flex-col gap-2">
      <div className="flex items-start gap-2">
        <span className="shrink-0 px-1.5 py-0.5 rounded bg-muted text-[10px] uppercase">{ev.source_type}</span>
        <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] ${confColor}`} title="Source confidence">
          {conf}
        </span>
        <div className="min-w-0 flex-1">
          <EvidenceSummary ev={ev} />
          {urls.length > 0 && (
            <div className="mt-1 space-y-0.5">
              {urls.slice(0, 3).map((u, i) => (
                <a key={i} href={u} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline block truncate flex items-center gap-1" title={u}>
                  <ExternalLink className="w-3 h-3 shrink-0" />
                  {u.length > 40 ? u.slice(0, 37) + '…' : u}
                </a>
              ))}
              {urls.length > 3 && <span className="text-muted-foreground">+{urls.length - 3} more</span>}
            </div>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-1 text-muted-foreground hover:text-foreground text-left"
      >
        {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        View raw
      </button>
      {expanded && (
        <pre className="text-[10px] bg-black/20 rounded p-2 overflow-auto max-h-48 whitespace-pre-wrap break-all">
          {JSON.stringify(ev?.raw ?? {}, null, 2)}
        </pre>
      )}
    </div>
  )
}

const nodeTypes = { entity: EntityNode }

const ENTITY_TYPES = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'username', label: 'Username' },
  { value: 'domain', label: 'Domain' },
  { value: 'person', label: 'Person' },
  { value: 'image_url', label: 'Image URL' },
  { value: 'url', label: 'URL' },
  { value: 'unknown', label: 'Unknown' },
]

const ENTITY_TYPE_COLORS: Record<string, string> = {
  email: '#3b82f6',
  phone: '#22c55e',
  username: '#f59e0b',
  domain: '#8b5cf6',
  person: '#ec4899',
  image_url: '#06b6d4',
  url: '#6366f1',
  account: '#1da1f2',
  post: '#657786',
  breach_hub: '#94a3b8',
  unknown: '#64748b',
}

function CaseListView({ onSelectCase }: { onSelectCase: (id: string) => void }) {
  const queryClient = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['playground-cases'],
    queryFn: () => apiClient.listPlaygroundCases(),
  })
  const createMutation = useMutation({
    mutationFn: ({ title, description }: { title: string; description?: string }) =>
      apiClient.createPlaygroundCase(title, description),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['playground-cases'] })
      onSelectCase(res.id)
    },
  })
  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deletePlaygroundCase(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['playground-cases'] }),
  })
  const [newTitle, setNewTitle] = useState('')
  const [newDesc, setNewDesc] = useState('')
  const [dialogOpen, setDialogOpen] = useState(false)

  const handleCreate = () => {
    if (!newTitle.trim()) return
    createMutation.mutate({ title: newTitle.trim(), description: newDesc || undefined })
    setNewTitle('')
    setNewDesc('')
    setDialogOpen(false)
  }

  if (isLoading) return <div className="p-6 text-muted-foreground">Loading cases...</div>

  const cases = data?.cases ?? []

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">OSINT Playground</h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              New Case
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Case</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label>Title</Label>
                <Input
                  placeholder="e.g. Email investigation"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Input
                  placeholder="Brief description"
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                />
              </div>
              <Button onClick={handleCreate} disabled={!newTitle.trim() || createMutation.isPending}>
                Create
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid gap-2">
        {cases.length === 0 ? (
          <p className="text-muted-foreground">No cases yet. Create one to start.</p>
        ) : (
          cases.map((c: any) => (
            <Card
              key={c.id}
              className="cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => {
                queryClient.invalidateQueries({ queryKey: ['playground-graph', c.id] })
                onSelectCase(c.id)
              }}
            >
              <CardContent className="flex items-center justify-between p-4">
                <div>
                  <p className="font-medium">{c.title}</p>
                  <p className="text-sm text-muted-foreground">
                    {c.entity_count ?? 0} entities · {c.relation_count ?? 0} relations
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation()
                    if (confirm('Delete this case?')) deleteMutation.mutate(c.id)
                  }}
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  )
}

function CaseView({ caseId, onBack }: { caseId: string; onBack: () => void }) {
  const queryClient = useQueryClient()
  const reactFlowInstance = useRef<any>(null)
  const [selectedNode, setSelectedNode] = useState<any>(null)
  const [addValue, setAddValue] = useState('')
  const [addType, setAddType] = useState('email')
  const [pasteText, setPasteText] = useState('')
  const [extractedEntities, setExtractedEntities] = useState<{ entity_type: string; value: string; canonical_value: string }[]>([])
  const [selectedExtracted, setSelectedExtracted] = useState<Set<number>>(new Set())
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; node: any } | null>(null)
  const [pendingConnection, setPendingConnection] = useState<{ source: string; target: string } | null>(null)
  const [relationLabel, setRelationLabel] = useState('')
  const [tgHandle, setTgHandle] = useState('')
  const [tgGroupUrl, setTgGroupUrl] = useState('')
  const [tgGroupType, setTgGroupType] = useState<'group' | 'channel'>('group')
  const [groupScrapeResult, setGroupScrapeResult] = useState<any>(null)
  const [groupScrapeOpen, setGroupScrapeOpen] = useState(false)
  const [expandedEvidenceId, setExpandedEvidenceId] = useState<string | null>(null)

  const { data: graphData, isLoading, refetch } = useQuery({
    queryKey: ['playground-graph', caseId],
    queryFn: () => apiClient.getPlaygroundGraph(caseId),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })
  const { data: entityDetail, isLoading: loadingEntity } = useQuery({
    queryKey: ['playground-entity', caseId, selectedNode?.id],
    queryFn: () => apiClient.getPlaygroundEntity(caseId, selectedNode!.id),
    enabled: !!selectedNode?.id,
  })
  const { data: entityPivots } = useQuery({
    queryKey: ['playground-entity-pivots', caseId, selectedNode?.id],
    queryFn: () => apiClient.getPlaygroundEntityPivots(caseId, selectedNode!.id),
    enabled: !!selectedNode?.id,
  })
  const [runningJobId, setRunningJobId] = useState<string | null>(null)
  const { data: caseJobs } = useQuery({
    queryKey: ['playground-jobs', caseId],
    queryFn: () => apiClient.listPlaygroundCaseJobs(caseId),
    refetchInterval: runningJobId ? 1500 : false,
  })
  const { data: jobStatus } = useQuery({
    queryKey: ['playground-job', runningJobId],
    queryFn: () => apiClient.getPlaygroundJob(runningJobId!),
    enabled: !!runningJobId,
    refetchInterval: runningJobId ? 1000 : false,
  })
  // Mirrors this page's own job-polling state into the app-wide background
  // jobs indicator, so a pivot still-running is visible (and its completion
  // still surfaces) after navigating away from this case — previously this
  // `runningJobId` was page-local state, so leaving the page lost all trace
  // of it even though it was still genuinely running server-side.
  const { startJob, completeJob, failJob } = useBackgroundJobs()
  const globalJobRef = useRef<string | null>(null)
  useEffect(() => {
    if (jobStatus?.status === 'success' || jobStatus?.status === 'failed') {
      setRunningJobId(null)
      queryClient.invalidateQueries({ queryKey: ['playground-graph', caseId] })
      queryClient.invalidateQueries({ queryKey: ['playground-entity', caseId, selectedNode?.id] })
      queryClient.invalidateQueries({ queryKey: ['playground-jobs', caseId] })
      if (globalJobRef.current) {
        if (jobStatus.status === 'success') completeJob(globalJobRef.current, { href: `/app/playground?case=${caseId}` })
        else failJob(globalJobRef.current, jobStatus.error || 'Pivot failed')
        globalJobRef.current = null
      }
    }
  }, [jobStatus?.status, jobStatus?.error, caseId, selectedNode?.id, queryClient, completeJob, failJob])

  const runPivotMutation = useMutation({
    mutationFn: ({ entityId, pivotId }: { entityId: string; pivotId: string }) =>
      apiClient.runPlaygroundPivot(caseId, entityId, pivotId),
    onSuccess: (res: any, variables) => {
      setRunningJobId(res?.id ?? null)
      globalJobRef.current = res?.id ? startJob(`Pivot: ${variables.pivotId}`) : null
      queryClient.invalidateQueries({ queryKey: ['playground-jobs', caseId] })
    },
  })
  const { toast } = useToast()
  const reprocessMutation = useMutation({
    mutationFn: (entityId: string) => apiClient.reprocessPlaygroundPivot(caseId, entityId),
    onSuccess: (data: { entities_added?: number; relations_added?: number }) => {
      queryClient.invalidateQueries({ queryKey: ['playground-graph', caseId] })
      queryClient.invalidateQueries({ queryKey: ['playground-entity', caseId, selectedNode?.id] })
      const e = data?.entities_added ?? 0
      const r = data?.relations_added ?? 0
      toast({
        title: 'Sync complete',
        description: e > 0 || r > 0 ? `Added ${e} entities and ${r} relations to the graph.` : 'No new entities or relations from evidence.',
      })
    },
  })

  const addEntityMutation = useMutation({
    mutationFn: (v: string) => apiClient.addPlaygroundEntity(caseId, v, addType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playground-graph', caseId] })
      setAddValue('')
    },
  })
  const extractMutation = useMutation({
    mutationFn: (text: string) => apiClient.extractPlaygroundEntities(text),
    onSuccess: (res: any) => {
      setExtractedEntities(res.entities ?? [])
      setSelectedExtracted(new Set((res.entities ?? []).map((_: any, i: number) => i)))
    },
  })
  const bulkAddMutation = useMutation({
    mutationFn: (entities: { value: string; entity_type: string }[]) =>
      apiClient.addPlaygroundEntitiesBulk(caseId, entities),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playground-graph', caseId] })
      setExtractedEntities([])
      setPasteText('')
    },
  })
  const addRelationMutation = useMutation({
    mutationFn: ({ sourceId, targetId, label }: { sourceId: string; targetId: string; label: string }) =>
      apiClient.addPlaygroundRelation(caseId, sourceId, targetId, label),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playground-graph', caseId] })
      setPendingConnection(null)
      setRelationLabel('')
    },
  })
  const deleteEntityMutation = useMutation({
    mutationFn: (entityId: string) => apiClient.deletePlaygroundEntity(caseId, entityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playground-graph', caseId] })
      queryClient.invalidateQueries({ queryKey: ['playground-entity', caseId, selectedNode?.id] })
      setSelectedNode(null)
      setContextMenu(null)
      toast({ title: 'Node deleted', description: 'Entity and its connections have been removed.' })
    },
  })

  const tgHandleAddLookupMutation = useMutation({
    mutationFn: async (input: string) => {
      const handles = input
        .split(/[\n,]+/)
        .map((s) => s.replace(/^@/, '').trim())
        .filter(Boolean)
      if (!handles.length) throw new Error('Enter one or more Telegram handles (@user1, @user2)')
      const results: { entity: { id: string }; job: { id: string } }[] = []
      for (const h of handles) {
        const value = h.startsWith('@') ? h : `@${h}`
        const entity = (await apiClient.addPlaygroundEntity(caseId, value, 'username')) as { id: string }
        const job = (await apiClient.runPlaygroundPivot(caseId, entity.id, 'telegram_reverse')) as { id: string }
        results.push({ entity, job })
      }
      return { results, lastJob: results[results.length - 1]?.job }
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['playground-graph', caseId] })
      queryClient.invalidateQueries({ queryKey: ['playground-jobs', caseId] })
      setRunningJobId(res.lastJob?.id ?? null)
      globalJobRef.current = res.lastJob?.id ? startJob(`Telegram reverse lookup: ${res.results.length} handle${res.results.length > 1 ? 's' : ''}`) : null
      setTgHandle('')
      toast({
        title: 'Added & lookup started',
        description: `${res.results.length} handle${res.results.length > 1 ? 's' : ''} added. Telegram reverse lookup running.`,
      })
    },
  })

  const groupScrapeMutation = useMutation({
    mutationFn: async ({ url, queryType }: { url: string; queryType: 'group' | 'channel' }) => {
      return apiClient.scrapeTelegramGroup(url.trim(), queryType) as Promise<any>
    },
    onSuccess: (result) => {
      setGroupScrapeResult(result)
      setGroupScrapeOpen(true)
    },
    onError: (err: any) => {
      setGroupScrapeResult({ success: false, error: err?.message || err?.detail || String(err) })
      setGroupScrapeOpen(true)
    },
  })

  const handleAddEntity = () => {
    if (!addValue.trim()) return
    addEntityMutation.mutate(addValue.trim())
  }

  const handleExtract = () => {
    if (!pasteText.trim()) return
    extractMutation.mutate(pasteText)
  }

  const toggleExtracted = (i: number) => {
    setSelectedExtracted((prev) => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return next
    })
  }

  const handleAddSelected = () => {
    const toAdd = extractedEntities.filter((_, i) => selectedExtracted.has(i))
    if (toAdd.length === 0) return
    bulkAddMutation.mutate(toAdd.map((e) => ({ value: e.value, entity_type: e.entity_type })))
  }

  const initialNodes = useMemo((): Node[] => {
    if (!graphData?.nodes?.length) return []
    const nodes = graphData.nodes
    const radius = nodes.length === 1 ? 0 : 150
    return nodes.map((n: any, i: number) => {
      const angle = (2 * Math.PI * i) / Math.max(nodes.length, 1)
      return {
        id: n.id,
        type: 'entity',
        position: { x: radius * Math.cos(angle), y: radius * Math.sin(angle) },
        data: {
          label: n.label || n.value,
          value: n.value,
          entity_type: n.entity_type,
          confidence: n.confidence,
        },
      }
    })
  }, [graphData])

  const initialEdges = useMemo((): Edge[] => {
    if (!graphData?.links?.length) return []
    return graphData.links.map((l: any, i: number) => ({
      id: l.id || `e-${l.source}-${l.target}-${i}`,
      source: l.source,
      target: l.target,
      label: l.label || 'related',
      labelShowBg: true,
      labelBgStyle: { fill: 'rgb(30 41 59)', stroke: 'rgb(71 85 105)' },
      labelBgPadding: [6, 4] as [number, number],
      labelBgBorderRadius: 4,
    }))
  }, [graphData])

  const handleConnect = useCallback((params: { source?: string | null; target?: string | null }) => {
    if (params.source && params.target) {
      setPendingConnection({ source: params.source, target: params.target })
      setRelationLabel('related')
    }
  }, [])
  const handleConfirmRelation = () => {
    if (!pendingConnection || !relationLabel.trim()) return
    addRelationMutation.mutate({
      sourceId: pendingConnection.source,
      targetId: pendingConnection.target,
      label: relationLabel.trim(),
    })
  }

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges)

  // Sync nodes/edges when graphData changes (e.g. after adding entity)
  useEffect(() => {
    setNodes(initialNodes)
    setEdges(initialEdges)
  }, [initialNodes, initialEdges, setNodes, setEdges])

  // Clear expanded evidence when switching nodes
  useEffect(() => {
    setExpandedEvidenceId(null)
  }, [selectedNode?.id])

  const onInit: OnInit = useCallback((instance) => {
    reactFlowInstance.current = instance
    instance.fitView({ padding: 0.2, duration: 300 })
  }, [])

  const handleFitView = useCallback(() => {
    reactFlowInstance.current?.fitView({ padding: 0.2, duration: 400 })
  }, [])

  const applyLayout = useCallback(
    (layout: 'circular' | 'grid' | 'horizontal') => {
      setNodes((nds) => {
        const n = nds.length
        if (n === 0) return nds
        const spacing = 180
        return nds.map((node, i) => {
          let pos = { x: 0, y: 0 }
          if (layout === 'circular') {
            const radius = n === 1 ? 0 : 150
            const angle = (2 * Math.PI * i) / Math.max(n, 1)
            pos = { x: radius * Math.cos(angle), y: radius * Math.sin(angle) }
          } else if (layout === 'grid') {
            const cols = Math.ceil(Math.sqrt(n))
            const row = Math.floor(i / cols)
            const col = i % cols
            pos = { x: col * spacing, y: row * spacing }
          } else {
            pos = { x: i * spacing, y: 0 }
          }
          return { ...node, position: pos }
        })
      })
    },
    [setNodes]
  )

  const handleDeleteNode = useCallback(
    (entityId: string) => {
      if (!confirm('Delete this node and all its connections?')) return
      deleteEntityMutation.mutate(entityId)
    },
    [deleteEntityMutation]
  )

  return (
    <div className="flex h-[calc(100vh-7rem)] w-full min-w-0 overflow-hidden">
      {/* Left: Input panel */}
      <div className="w-80 shrink-0 border-r bg-card p-4 flex flex-col gap-4 overflow-auto">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            queryClient.invalidateQueries({ queryKey: ['playground-cases'] })
            onBack()
          }}
        >
          ← Back to cases
        </Button>

        <p className="text-xs text-muted-foreground">
          Drag from a node&apos;s bottom handle to another&apos;s top handle to connect, then add a relationship label.
        </p>
        {/* Add single entity */}
        <div>
          <Label>Add entity</Label>
          <div className="flex gap-2 mt-2">
            <Input
              placeholder="email@example.com"
              value={addValue}
              onChange={(e) => setAddValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddEntity()}
            />
            <Select value={addType} onValueChange={setAddType}>
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ENTITY_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            className="mt-2 w-full"
            onClick={handleAddEntity}
            disabled={!addValue.trim() || addEntityMutation.isPending}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add
          </Button>
        </div>

        {/* Paste to extract */}
        <div>
          <Label className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Paste text → extract
          </Label>
          <Textarea
            className="mt-2 min-h-[80px]"
            placeholder="Paste paragraph with emails, phones, domains, @handles..."
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
          />
          <Button
            className="mt-2 w-full"
            variant="secondary"
            onClick={handleExtract}
            disabled={!pasteText.trim() || extractMutation.isPending}
          >
            Extract
          </Button>
          {extractedEntities.length > 0 && (
            <div className="mt-3 space-y-2">
              <p className="text-xs text-muted-foreground">
                {extractedEntities.length} found. Select to add:
              </p>
              <div className="max-h-32 overflow-auto space-y-1">
                {extractedEntities.map((e, i) => (
                  <label
                    key={i}
                    className="flex items-center gap-2 text-xs cursor-pointer hover:bg-muted/50 rounded px-2 py-1"
                  >
                    <input
                      type="checkbox"
                      checked={selectedExtracted.has(i)}
                      onChange={() => toggleExtracted(i)}
                    />
                    <span className="text-muted-foreground">{e.entity_type}:</span>
                    <span className="truncate">{e.value}</span>
                  </label>
                ))}
              </div>
              <Button
                size="sm"
                className="w-full"
                onClick={handleAddSelected}
                disabled={selectedExtracted.size === 0 || bulkAddMutation.isPending}
              >
                Add selected ({selectedExtracted.size})
              </Button>
            </div>
          )}
        </div>

        {/* Telegram tools */}
        <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
          <Label className="flex items-center gap-2 text-sm font-medium">
            <Send className="w-4 h-4" />
            Telegram
          </Label>
          <div>
            <p className="text-xs text-muted-foreground mb-1">TG handle lookup (comma or newline for multiple)</p>
            <Textarea
              placeholder="@username or user1, user2, user3"
              value={tgHandle}
              onChange={(e) => setTgHandle(e.target.value)}
              className="min-h-[60px] text-sm resize-none"
              rows={2}
            />
            <Button
              size="sm"
              className="w-full mt-2"
              onClick={() => tgHandle.trim() && tgHandleAddLookupMutation.mutate(tgHandle)}
              disabled={!tgHandle.trim() || tgHandleAddLookupMutation.isPending}
            >
              {tgHandleAddLookupMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Add & lookup
            </Button>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">Group/channel export (via @xepli_robot)</p>
            <Input
              placeholder="https://t.me/group or @group"
              value={tgGroupUrl}
              onChange={(e) => setTgGroupUrl(e.target.value)}
              className="mb-2"
            />
            <div className="flex gap-2">
              <Select value={tgGroupType} onValueChange={(v: 'group' | 'channel') => setTgGroupType(v)}>
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="group">Group</SelectItem>
                  <SelectItem value="channel">Channel</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                className="flex-1"
                variant="secondary"
                onClick={() => tgGroupUrl.trim() && groupScrapeMutation.mutate({ url: tgGroupUrl, queryType: tgGroupType })}
                disabled={!tgGroupUrl.trim() || groupScrapeMutation.isPending}
              >
                {groupScrapeMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Users className="w-4 h-4 mr-2" />
                )}
                Export participants
              </Button>
            </div>
          </div>
        </div>

        {/* Group scrape results modal */}
        <Dialog open={groupScrapeOpen} onOpenChange={setGroupScrapeOpen}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Group participants</DialogTitle>
            </DialogHeader>
            {groupScrapeResult && (
              <GroupScrapeResults
                result={groupScrapeResult}
                caseId={caseId}
                onAddToGraph={() => {
                  const participants = groupScrapeResult?.json_file?.participants ?? []
                  if (participants.length === 0) return
                  const toAdd: { value: string; entity_type: string }[] = []
                  const seen = new Set<string>()
                  for (const p of participants) {
                    const un = (p.username || '').trim()
                    if (un && !seen.has(un.toLowerCase())) {
                      seen.add(un.toLowerCase())
                      toAdd.push({ value: un.startsWith('@') ? un : `@${un}`, entity_type: 'username' })
                    }
                    const name = [p.first_name, p.last_name].filter(Boolean).join(' ').trim()
                    if (name && name.length >= 2 && !seen.has(`person:${name}`)) {
                      seen.add(`person:${name}`)
                      toAdd.push({ value: name, entity_type: 'person' })
                    }
                  }
                  if (toAdd.length > 0) {
                    bulkAddMutation.mutate(toAdd)
                    toast({ title: 'Added to graph', description: `${toAdd.length} entities added.` })
                    setGroupScrapeOpen(false)
                  }
                }}
                onClose={() => setGroupScrapeOpen(false)}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Center: Graph */}
      <div
        className="flex-1 min-w-0 relative bg-slate-950 overflow-hidden"
        onContextMenu={(e) => e.preventDefault()}
        onClick={() => setContextMenu(null)}
      >
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
            Loading graph...
          </div>
        ) : nodes.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
            No entities yet. Add one or paste text to extract.
          </div>
        ) : (
          <div className="absolute inset-0 w-full h-full">
            <div className="absolute top-2 left-2 right-2 flex items-center justify-between z-10 gap-2">
              <div className="flex flex-wrap items-center gap-2">
                {['email', 'phone', 'username', 'domain', 'url', 'breach_hub'].map((t) => (
                  <span
                    key={t}
                    className="flex items-center gap-1.5 px-2 py-1 rounded text-xs bg-black/60 text-white"
                  >
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: ENTITY_TYPE_COLORS[t] ?? '#64748b' }}
                    />
                    {t === 'breach_hub' ? 'Breach hub' : t}
                  </span>
                ))}
                <span className="text-xs text-muted-foreground">{nodes.length} nodes</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => refetch()}
                  title="Refresh graph"
                >
                  ↻
                </Button>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => applyLayout('circular')}
                    title="Circular layout"
                  >
                    <Circle className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => applyLayout('grid')}
                    title="Grid layout"
                  >
                    <LayoutGrid className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => applyLayout('horizontal')}
                    title="Horizontal layout"
                  >
                    <Rows className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleFitView}
                title="Center view on nodes"
              >
                <Maximize2 className="w-4 h-4 mr-1" />
                Center
              </Button>
            </div>
            <ReactFlow
              nodes={nodes}
              edges={edges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onInit={onInit}
              onConnect={handleConnect}
              onNodeClick={(_: any, node: any) => {
                setSelectedNode({ id: node.id, ...node.data })
                setContextMenu(null)
              }}
              onNodeContextMenu={(e: any, node: any) => {
                e.preventDefault()
                const nodeData = { id: node.id, ...node.data }
                setSelectedNode(nodeData)
                setContextMenu({ x: e.clientX, y: e.clientY, node: nodeData })
              }}
              onPaneClick={() => setContextMenu(null)}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
              minZoom={0.1}
              maxZoom={2}
              className="bg-slate-950"
            >
              <Background color="#334155" gap={16} />
              <Controls className="!bg-card !border-border" />
              <MiniMap
                nodeColor={(n) => ENTITY_TYPE_COLORS[n.data?.entity_type] ?? '#64748b'}
                maskColor="rgb(15, 23, 42 / 0.8)"
                className="!bg-slate-900"
              />
            </ReactFlow>
          </div>
        )}
        {/* Relation label dialog - when user connects two nodes */}
        <Dialog open={!!pendingConnection} onOpenChange={(open) => !open && setPendingConnection(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Link2 className="w-4 h-4" />
                Add relationship
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground">
                Enter a label for the relationship between these two nodes.
              </p>
              <div>
                <Label>Relationship label</Label>
                <Input
                  placeholder="e.g. knows, works_at, exposed_in"
                  value={relationLabel}
                  onChange={(e) => setRelationLabel(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleConfirmRelation()}
                  className="mt-2"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPendingConnection(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmRelation}
                  disabled={!relationLabel.trim() || addRelationMutation.isPending}
                >
                  {addRelationMutation.isPending ? 'Adding...' : 'Add'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        {contextMenu && (
          <div
            className="fixed bg-card border rounded shadow-lg py-1 z-50 min-w-[180px]"
            style={{ left: contextMenu.x, top: contextMenu.y }}
          >
            {[
              contextMenu.node?.entity_type === 'domain' && { pivotId: 'whois', label: 'Run WHOIS' },
              ['email', 'phone'].includes(contextMenu.node?.entity_type) && { pivotId: 'breach', label: 'Run Breach Check' },
              ['email', 'phone', 'username', 'domain', 'person'].includes(contextMenu.node?.entity_type) && {
                pivotId: 'serp',
                label: 'Run Web Search',
              },
              contextMenu.node?.entity_type === 'email' && { pivotId: 'epieos', label: 'Run Gmail OSINT' },
              contextMenu.node?.entity_type === 'email' && { pivotId: 'rocketreach_enrich', label: 'Run RocketReach Enrich' },
              contextMenu.node?.entity_type === 'url' &&
                String(contextMenu.node?.value || '').includes('linkedin.com/in/') && {
                  pivotId: 'rocketreach_linkedin',
                  label: 'Run RocketReach LinkedIn',
                },
              contextMenu.node?.entity_type === 'person' && { pivotId: 'rocketreach_search', label: 'Run RocketReach Search' },
              contextMenu.node?.entity_type === 'phone' && { pivotId: 'eyecon', label: 'Run Eyecon Caller ID' },
              contextMenu.node?.entity_type === 'username' && { pivotId: 'telegram_reverse', label: 'Run Telegram Reverse' },
              contextMenu.node?.entity_type === 'username' && { pivotId: 'x_profile', label: 'Run X Profile' },
              contextMenu.node?.entity_type === 'username' && { pivotId: 'x_posts_recent', label: 'Run X Recent Posts' },
              contextMenu.node?.entity_type === 'username' && { pivotId: 'reddit_user', label: 'Run Reddit User' },
              contextMenu.node?.entity_type === 'username' && { pivotId: 'ig_profile', label: 'Run Instagram Profile' },
              contextMenu.node?.entity_type === 'username' && { pivotId: 'tiktok_profile', label: 'Run TikTok Profile' },
            ]
              .filter(Boolean)
              .map((p: any) => (
                <button
                  key={p.pivotId}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center gap-2"
                  onClick={() => {
                    runPivotMutation.mutate({ entityId: contextMenu!.node.id, pivotId: p.pivotId })
                    setContextMenu(null)
                  }}
                  disabled={runPivotMutation.isPending}
                >
                  <Zap className="w-4 h-4" />
                  {p.label}
                </button>
              ))}
            {![
              'domain',
              'email',
              'phone',
              'username',
              'person',
              'account',
              'post',
            ].includes(contextMenu.node?.entity_type) &&
              contextMenu.node?.entity_type !== 'breach_hub' && (
                <p className="px-4 py-2 text-xs text-muted-foreground">No pivots for this type</p>
              )}
            <div className="border-t my-1" />
            <button
              className="w-full px-4 py-2 text-left text-sm hover:bg-muted flex items-center gap-2 text-destructive"
              onClick={() => {
                handleDeleteNode(contextMenu!.node.id)
              }}
              disabled={deleteEntityMutation.isPending}
            >
              <Trash2 className="w-4 h-4" />
              Delete node
            </button>
          </div>
        )}
      </div>

      {/* Right: Node inspector */}
      <div className="w-80 shrink-0 border-l bg-card p-4 overflow-auto">
        <h3 className="font-medium mb-2 flex items-center gap-2">
          <MousePointer2 className="w-4 h-4" />
          Node inspector
        </h3>
        {selectedNode ? (
          <div className="space-y-3 text-sm">
            {loadingEntity ? (
              <p className="text-muted-foreground">Loading...</p>
            ) : entityDetail ? (
              <>
                <div>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p>
                        <span className="text-muted-foreground">Value:</span> {entityDetail.value}
                      </p>
                      <p>
                        <span className="text-muted-foreground">Type:</span> {entityDetail.entity_type}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleDeleteNode(selectedNode!.id)}
                      disabled={deleteEntityMutation.isPending}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      Delete
                    </Button>
                  </div>
                  <p>
                    <span className="text-muted-foreground">Confidence:</span>{' '}
                    <span
                      className={
                        entityDetail.confidence === 'high'
                          ? 'text-green-600'
                          : entityDetail.confidence === 'medium'
                            ? 'text-amber-600'
                            : 'text-muted-foreground'
                      }
                    >
                      {entityDetail.confidence}
                    </span>
                  </p>
                  {/* Phase 4: Person profile — merged attributes from linked entities */}
                  {entityDetail.entity_type === 'person' && graphData?.links && (() => {
                    const pid = selectedNode!.id
                    const byLabel: Record<string, string[]> = {}
                    for (const l of graphData.links) {
                      const isSource = l.source === pid
                      const isTarget = l.target === pid
                      if (!isSource && !isTarget) continue
                      const otherId = isSource ? l.target : l.source
                      const otherNode = graphData.nodes?.find((n: any) => n.id === otherId)
                      const label = otherNode?.label ?? otherNode?.value ?? otherId
                      if (!label) continue
                      const key = l.label || 'related'
                      if (!byLabel[key]) byLabel[key] = []
                      if (!byLabel[key].includes(label)) byLabel[key].push(label)
                    }
                    const labels: Record<string, string> = {
                      OWNS_EMAIL: 'Emails',
                      OWNS_PHONE: 'Phones',
                      USES_HANDLE: 'Handles',
                      WORKS_AT: 'Employers',
                    }
                    const entries = Object.entries(byLabel).filter(([, v]) => v.length > 0)
                    if (entries.length === 0) return null
                    return (
                      <div className="mt-2 space-y-2 rounded border p-2 bg-muted/20">
                        <p className="text-muted-foreground text-xs font-medium">Person profile</p>
                        {entries.map(([label, arr]) => (
                          <div key={label}>
                            <p className="text-[10px] text-muted-foreground uppercase">{labels[label] ?? label}</p>
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {arr.map((v) => (
                                <span key={v} className="px-1.5 py-0.5 rounded text-xs bg-muted" title={label}>
                                  {v}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  })()}
                  {/* Phase 4: Employment timeline from RocketReach */}
                  {entityDetail.entity_type === 'person' && (() => {
                    const jobHistory: { title?: string; employer?: string; start?: string; end?: string }[] = []
                    for (const ev of entityDetail.evidence ?? []) {
                      const raw = ev?.raw || {}
                      const pid = raw.pivot_id || ''
                      if (!pid.startsWith('rocketreach')) continue
                      const jh = raw.job_history
                      if (Array.isArray(jh)) {
                        for (const j of jh) {
                          jobHistory.push({
                            title: j.title ?? j.name,
                            employer: j.employer ?? j.company ?? j.company_name,
                            start: j.start_date ?? j.start,
                            end: j.end_date ?? j.end ?? (j.current ? 'Current' : undefined),
                          })
                        }
                      }
                    }
                    if (jobHistory.length === 0) return null
                    return (
                      <div className="mt-2 space-y-2 rounded border p-2 bg-muted/20">
                        <p className="text-muted-foreground text-xs font-medium">Employment timeline</p>
                        <div className="space-y-2 max-h-32 overflow-auto">
                          {jobHistory.slice(0, 10).map((j, i) => (
                            <div key={i} className="text-xs border-l-2 border-muted pl-2">
                              <div className="font-medium">{j.title || 'Role'}</div>
                              {j.employer && <div className="text-muted-foreground">{j.employer}</div>}
                              {(j.start || j.end) && (
                                <div className="text-[10px] text-muted-foreground">
                                  {[j.start, j.end].filter(Boolean).join(' – ')}
                                </div>
                              )}
                            </div>
                          ))}
                          {jobHistory.length > 10 && <span className="text-[10px] text-muted-foreground">+{jobHistory.length - 10} more</span>}
                        </div>
                      </div>
                    )
                  })()}
                  {/* Linked entities (generic) */}
                  {entityDetail.entity_type !== 'person' && graphData?.links && (() => {
                    const connected = graphData.links
                      .filter((l: any) => l.source === selectedNode!.id || l.target === selectedNode!.id)
                      .map((l: any) => {
                        const otherId = l.source === selectedNode!.id ? l.target : l.source
                        const otherNode = graphData.nodes?.find((n: any) => n.id === otherId)
                        return { id: otherId, label: otherNode?.label ?? otherId, edgeLabel: l.label }
                      })
                      .filter((x: any) => x.label)
                    if (connected.length === 0) return null
                    return (
                      <div className="mt-2">
                        <p className="text-muted-foreground text-xs mb-1">Linked ({connected.length})</p>
                        <div className="flex flex-wrap gap-1">
                          {connected.slice(0, 8).map((c: any) => (
                            <span key={c.id} className="inline-block px-1.5 py-0.5 rounded text-xs bg-muted" title={c.edgeLabel}>
                              {c.label}
                            </span>
                          ))}
                          {connected.length > 8 && <span className="text-xs text-muted-foreground">+{connected.length - 8}</span>}
                        </div>
                      </div>
                    )
                  })()}
                  {(entityDetail.evidence?.length ?? 0) > 0 && (
                    <p className="mt-1">
                      <span className="text-muted-foreground">Sources:</span>{' '}
                      {[...new Set((entityDetail.evidence ?? []).map((e: any) => e.source_type))].map((s: string) => (
                        <span key={s} className="inline-block px-1.5 py-0.5 rounded text-xs bg-muted mr-1">
                          {s}
                        </span>
                      ))}
                    </p>
                  )}
                </div>
                {/* Available pivots */}
                {(entityPivots?.pivots?.length ?? 0) > 0 && (
                  <div>
                    <p className="text-muted-foreground mb-2 flex items-center gap-2">
                      <Zap className="w-4 h-4" />
                      Available pivots
                    </p>
                    <div className="space-y-2">
                      {entityPivots!.pivots.map((p: any) => (
                        <div key={p.pivot_id} className="rounded border p-2 bg-muted/30">
                          <p className="font-medium text-xs">{p.name}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{p.description}</p>
                          <Button
                            size="sm"
                            variant="secondary"
                            className="mt-2 h-7 text-xs"
                            disabled={
                              runPivotMutation.isPending ||
                              (runningJobId !== null && jobStatus?.status !== 'success' && jobStatus?.status !== 'failed')
                            }
                            onClick={() =>
                              runPivotMutation.mutate({ entityId: selectedNode!.id, pivotId: p.pivot_id })
                            }
                          >
                            {runPivotMutation.isPending && runPivotMutation.variables?.pivotId === p.pivot_id
                              ? 'Running...'
                              : 'Run'}
                          </Button>
                        </div>
                      ))}
                    </div>
                    {runningJobId && (
                      <p className="text-xs text-amber-600 mt-2">
                        Job {jobStatus?.status === 'running' ? 'running' : jobStatus?.status}...
                      </p>
                    )}
                  </div>
                )}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-muted-foreground">Evidence ({entityDetail.evidence?.length ?? 0})</p>
                    {(entityDetail.evidence ?? []).some((e: any) => e.source_type === 'pivot') && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs gap-1"
                        disabled={reprocessMutation.isPending}
                        onClick={() => reprocessMutation.mutate(selectedNode!.id)}
                      >
                        <RefreshCw className={`w-3 h-3 ${reprocessMutation.isPending ? 'animate-spin' : ''}`} />
                        {reprocessMutation.isPending ? 'Syncing...' : 'Sync to graph'}
                      </Button>
                    )}
                  </div>
                  <div className="space-y-2 max-h-48 overflow-auto">
                    {(entityDetail.evidence ?? []).map((ev: any) => (
                      <EvidenceCard
                        key={ev.id}
                        ev={ev}
                        expanded={expandedEvidenceId === ev.id}
                        onToggle={() => setExpandedEvidenceId((x) => (x === ev.id ? null : ev.id))}
                      />
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <p className="text-muted-foreground">Could not load entity</p>
            )}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">Click a node to inspect</p>
        )}
        {/* Activity log — jobs in progress, results */}
        <div className="mt-4 pt-4 border-t">
          <p className="text-muted-foreground text-xs font-medium mb-2 flex items-center gap-2">
            <Zap className="w-3.5 h-3.5" />
            Activity log
          </p>
          <div className="space-y-2 max-h-48 overflow-auto">
            <AnimatePresence mode="popLayout">
              {/* Running job (live) */}
              {runningJobId && jobStatus && (
                <motion.div
                  key={`running-${runningJobId}`}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-2.5 flex items-center gap-3"
                >
                  <Loader2 className="w-4 h-4 shrink-0 animate-spin text-amber-500" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                      {(jobStatus as any).pivot_id || 'Pivot'} running...
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Enriching selected node
                    </p>
                  </div>
                </motion.div>
              )}
              {/* Job history */}
              {(caseJobs?.jobs ?? []).slice(0, 8).map((j: any) => (
                <motion.div
                  key={j.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`rounded-lg border p-2.5 flex items-start gap-3 ${
                    j.status === 'success'
                      ? 'border-green-500/30 bg-green-500/5'
                      : j.status === 'failed'
                        ? 'border-red-500/30 bg-red-500/5'
                        : 'border-muted bg-muted/20'
                  }`}
                >
                  {j.status === 'success' ? (
                    <CheckCircle2 className="w-4 h-4 shrink-0 text-green-600 mt-0.5" />
                  ) : j.status === 'failed' ? (
                    <XCircle className="w-4 h-4 shrink-0 text-red-600 mt-0.5" />
                  ) : (
                    <Loader2 className="w-4 h-4 shrink-0 animate-spin text-muted-foreground mt-0.5" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium capitalize">
                      {j.pivot_id} — {j.status}
                    </p>
                    {j.status === 'success' && j.result && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        +{j.result.entities_added ?? 0} entities, +{j.result.relations_added ?? 0} relations
                      </p>
                    )}
                    {j.status === 'failed' && j.error && (
                      <p className="text-[10px] text-red-600 truncate mt-0.5" title={j.error}>
                        {j.error}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            {(!caseJobs?.jobs?.length && !runningJobId) && (
              <p className="text-xs text-muted-foreground py-2">No pivot activity yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Playground() {
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null)

  if (selectedCaseId) {
    return (
      <CaseView caseId={selectedCaseId} onBack={() => setSelectedCaseId(null)} />
    )
  }
  return <CaseListView onSelectCase={setSelectedCaseId} />
}
